import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/api";
import { LayoutDashboard, Grid3x3, List, ExternalLink, Loader2 } from "lucide-react";

function DashboardPage() {
  const navigate = useNavigate();
  const [dashboardItems, setDashboardItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [viewMode, setViewMode] = useState("card");

  useEffect(() => {
    const id = sessionStorage.getItem("userId") || localStorage.getItem("userId");
    if (!id) {
      navigate("/");
      return;
    }
    loadDashboardData(id);
  }, [navigate]);

  // ESC key handler to go back to explorer
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        navigate("/explorer");
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [navigate]);

  const loadDashboardData = async (uid) => {
    try {
      setLoading(true);
      setError("");
      const resp = await api.GetAllSubMenuList(uid);
      
      // Filter only items with IsDashBoard = 1
      const allItems = resp?.Table || [];
      const dashboardOnly = allItems.filter(item => item.IsDashBoard === 1);
      
      setDashboardItems(dashboardOnly);
    } catch (err) {
      console.error("Failed to load dashboard data:", err);
      setError(err?.message || "Failed to load dashboard data");
      setDashboardItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleItemClick = (item) => {
    if (!item.ReactWebPath) return;
    
    const cleanPath = item.ReactWebPath.trim();
    const separator = cleanPath.includes('?') ? '&' : '?';
    let url = `${cleanPath}${separator}mdlId=${item.MdlId}`;
    if (item.TxnType) url += `&TxnType=${encodeURIComponent(item.TxnType)}`;
    if (item.TxnSubType) url += `&TxnSubType=${encodeURIComponent(item.TxnSubType)}`;
    
    window.location.href = url;
  };

  // Group dashboard items by Mnu_Group
  const groupedItems = dashboardItems.reduce((acc, item) => {
    const group = item.Mnu_Group || "Other";
    if (!acc[group]) acc[group] = [];
    acc[group].push(item);
    return acc;
  }, {});

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: 'var(--base-200)' }}
    >
      {/* Top Bar */}
      <div className="fixed top-0 left-0 right-0 z-20 flex items-center justify-between p-4 shadow-lg" style={{ backgroundColor: 'var(--base-100)', borderBottom: '1px solid var(--base-300)' }}>
        <button onClick={() => navigate("/explorer")} className="px-4 py-2 text-sm font-medium text-gray-700 transition-colors bg-gray-100 rounded-md hover:bg-gray-200">
          ← Back to Explorer
        </button>
        <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
          <LayoutDashboard size={24} />
          Dashboard
        </h1>
        <div className="flex items-center gap-1 p-1 rounded-lg bg-slate-100">
          <button
            onClick={() => setViewMode("card")}
            className={`p-2 rounded-md transition-all ${
              viewMode === "card"
                ? "bg-white text-primary shadow-sm"
                : "text-slate-600 hover:text-slate-800"
            }`}
            title="Card View"
          >
            <Grid3x3 size={18} />
          </button>
          <button
            onClick={() => setViewMode("table")}
            className={`p-2 rounded-md transition-all ${
              viewMode === "table"
                ? "bg-white text-primary shadow-sm"
                : "text-slate-600 hover:text-slate-800"
            }`}
            title="Table View"
          >
            <List size={18} />
          </button>
        </div>
      </div>

      <div className="pt-20 px-4 md:px-8 pb-8 min-h-[calc(100vh-5rem)]">

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg">
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 size={40} className="animate-spin text-primary mb-4" />
            <p className="text-slate-500">Loading dashboard items...</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && dashboardItems.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-slate-400">
            <LayoutDashboard size={48} className="mb-4 opacity-30" />
            <p className="text-lg font-medium">No Dashboard Items</p>
            <p className="text-sm mt-1">There are no items marked as dashboard</p>
          </div>
        )}

        {/* Card View */}
        {!loading && viewMode === "card" && dashboardItems.length > 0 && (
          <div className="space-y-6">
            {Object.entries(groupedItems).map(([group, items]) => (
              <div key={group}>
                {/* <h2 className="text-lg font-semibold text-slate-700 mb-3 pb-2 border-b border-slate-200">
                  {group}
                  <span className="ml-2 text-sm font-normal text-slate-500">
                    ({items.length} {items.length === 1 ? 'item' : 'items'})
                  </span>
                </h2> */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {items.map((item, idx) => (
                    <div
                      key={idx}
                      onClick={() => handleItemClick(item)}
                      className="p-4 bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md hover:border-primary/30 transition-all cursor-pointer group"
                    >
                      <div className="flex flex-col items-center text-center">
                        {item.logoFile ? (
                          <div className="w-16 h-16 mb-3 rounded-lg overflow-hidden bg-slate-50 flex items-center justify-center p-2">
                            <img 
                              src={`/assets/DashboardImages/${item.logoFile}`} 
                              alt={item.MdlDisplayName}
                              className="max-w-full max-h-full object-contain"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                            <div className="hidden w-full h-full items-center justify-center">
                              <LayoutDashboard size={28} className="text-primary" />
                            </div>
                          </div>
                        ) : (
                          <div className="w-16 h-16 mb-3 rounded-lg bg-primary/10 flex items-center justify-center">
                            <LayoutDashboard size={28} className="text-primary" />
                          </div>
                        )}
                        <h3 className="font-semibold text-slate-800 group-hover:text-primary transition-colors">
                          {item.MdlDisplayName} 
                        </h3>
                        <p className="text-xs text-slate-500 mt-1">
                          {item.MdlType}
                        </p>
                      </div>
                      
                      <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-center">                  
                        <span className="flex items-center gap-1 text-xs text-primary font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                          Open <ExternalLink size={12} />
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Table View */}
        {!loading && viewMode === "table" && dashboardItems.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Logo
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Module Name
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {dashboardItems.map((item, idx) => (
                    <tr
                      key={idx}
                      onClick={() => handleItemClick(item)}
                      className="hover:bg-slate-50 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3">
                        {item.logoFile ? (
                          <div className="w-10 h-10 rounded-lg overflow-hidden bg-slate-50 flex items-center justify-center">
                            <img 
                              src={`/assets/DashboardImages/${item.logoFile}`} 
                              alt={item.MdlDisplayName}
                              className="max-w-full max-h-full object-contain"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                            <div className="hidden w-full h-full items-center justify-center">
                              <LayoutDashboard size={16} className="text-primary" />
                            </div>
                          </div>
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                            <LayoutDashboard size={16} className="text-primary" />
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-medium text-slate-800">
                          {item.MdlDisplayName}
                        </span>
                      </td>

                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-slate-100 text-slate-600">
                          {item.MdlType}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button className="p-1.5 rounded hover:bg-primary/10 text-primary transition-colors">
                          <ExternalLink size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default DashboardPage;
